import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * Editable list of strings (optionally numbered). Add / edit / remove rows.
 * Parent owns the array via [items]/(itemsChange).
 */
@Component({
  selector: 'bb-edit-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2">
      @for (item of items(); track $index) {
        <div class="flex items-start gap-2.5">
          @if (numbered()) {
            <span class="w-5 flex-shrink-0 pt-2.5 font-heading text-lg font-black text-brand-studio">{{ $index + 1 }}</span>
          } @else {
            <span class="mt-3.5 size-1.5 flex-shrink-0 rounded-full bg-muted-foreground" aria-hidden="true"></span>
          }
          <input
            [value]="item"
            (input)="updateAt($index, $any($event.target).value)"
            [placeholder]="placeholder()"
            class="flex-1 rounded-md border border-border bg-card px-3.5 py-2.5 text-sm outline-none focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
          <button
            type="button"
            aria-label="Remove item"
            class="grid size-9 flex-shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            (click)="removeAt($index)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      }
      <button
        type="button"
        class="inline-flex items-center gap-2 self-start rounded-md border border-border px-3.5 py-2 text-sm font-semibold text-foreground transition-colors hover:border-brand-studio hover:text-brand-studio"
        (click)="add()">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14" /><path d="M12 5v14" />
        </svg>
        {{ addLabel() }}
      </button>
    </div>
  `,
})
export class BbEditList {
  readonly items = input<string[]>([]);
  readonly numbered = input<boolean>(false);
  readonly placeholder = input<string>('');
  readonly addLabel = input<string>('Add item');
  readonly itemsChange = output<string[]>();

  updateAt(index: number, value: string): void {
    this.itemsChange.emit(this.items().map((item, i) => (i === index ? value : item)));
  }

  removeAt(index: number): void {
    this.itemsChange.emit(this.items().filter((_, i) => i !== index));
  }

  add(): void {
    this.itemsChange.emit([...this.items(), '']);
  }
}
